import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { MARQUEE } from "../../data/content";
import { MaskLines, Overline, Reveal } from "./Motion";

export const Marquee = ({ items = MARQUEE }) => (
  <div data-testid="editorial-marquee" className="marquee overflow-hidden border-y border-black/10 py-6 sm:py-8">
    <div className="marquee-track flex">
      {[...items, ...items].map((t, i) => (
        <span key={i} className="flex items-center whitespace-nowrap px-6 text-3xl tracking-tight sm:text-5xl">
          <span className={i % 2 ? "font-serif-i" : "font-semibold"}>{t}</span>
          <span className="ml-12 inline-block h-2.5 w-2.5 rotate-45 bg-[var(--cobalt)]" />
        </span>
      ))}
    </div>
  </div>
);

export const PageHero = ({ label, lines, intro, image, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} data-testid={testid} className="relative overflow-hidden pt-32 sm:pt-40">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Overline>{label}</Overline>
        <h1 className="mt-6 text-5xl font-semibold leading-[0.95] tracking-[-0.04em] sm:text-7xl lg:text-8xl">
          <MaskLines lines={lines} />
        </h1>
        <div className="mt-10 grid gap-8 lg:grid-cols-12">
          <Reveal delay={0.4} className="lg:col-span-5 lg:col-start-8">
            <p className="text-base leading-relaxed text-[var(--muted)] sm:text-lg">{intro}</p>
          </Reveal>
        </div>
      </div>
      {image && (
        <div className="mx-auto mt-14 max-w-[1400px] px-5 sm:px-8">
          <motion.div
            initial={{ clipPath: "inset(12% 8% 12% 8% round 4px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 4px)" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            className="relative h-[42vh] overflow-hidden sm:h-[60vh]"
          >
            <motion.img style={{ y }} src={image} alt="" className="absolute inset-0 h-[120%] w-full object-cover grayscale-[35%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </motion.div>
        </div>
      )}
    </section>
  );
};

export const CTABand = () => (
  <section data-testid="cta-band" className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
    <div className="relative overflow-hidden rounded-[4px] bg-[var(--cobalt)] px-6 py-16 text-white sm:px-14 sm:py-24 grain">
      <svg className="spin-slow pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] opacity-25" viewBox="0 0 200 200" aria-hidden="true">
        {[90, 70, 50, 30].map((r) => <circle key={r} cx="100" cy="100" r={r} fill="none" stroke="white" strokeWidth="0.6" strokeDasharray="2 4" />)}
        <line x1="0" y1="100" x2="200" y2="100" stroke="white" strokeWidth="0.6" />
        <line x1="100" y1="0" x2="100" y2="200" stroke="white" strokeWidth="0.6" />
      </svg>
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-white/80">Have a question worth answering?</p>
      <h2 className="relative mt-6 max-w-4xl text-4xl font-semibold leading-[1] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
        <MaskLines inView lines={["Let's find the answer", <span className="font-serif-i">your market is hiding.</span>]} />
      </h2>
      <Link
        to="/contact"
        data-testid="cta-band-button"
        className="group relative mt-10 inline-flex items-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-semibold text-[var(--ink)] transition-transform hover:scale-[1.03]"
      >
        Request a proposal
        <span className="grid h-7 w-7 place-items-center rounded-full bg-[var(--ink)] text-white transition-transform group-hover:rotate-45">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </Link>
    </div>
  </section>
);

export const SectionHead = ({ label, title, aside, className = "" }) => (
  <div className={`grid gap-6 lg:grid-cols-12 lg:items-end ${className}`}>
    <div className="lg:col-span-8">
      <Overline>{label}</Overline>
      <h2 className="mt-5 text-4xl font-semibold leading-[1] tracking-[-0.035em] sm:text-5xl lg:text-6xl">
        <MaskLines inView lines={title} />
      </h2>
    </div>
    {aside && (
      <Reveal className="lg:col-span-4">
        <p className="text-base leading-relaxed text-[var(--muted)]">{aside}</p>
      </Reveal>
    )}
  </div>
);
